import Bugsnag from '@bugsnag/js'
import { Box, CircularProgress, InputAdornment, Typography } from '@mui/material'
import AdxButton from 'components/custom/AdxButton'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxInput from 'components/custom/AdxInput'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import EmployeeList from 'module/payroll/global-components/EmployeeList'
import React, { useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import {
  adjustMtdCalculation,
  recalculateSelectedEmployee,
} from 'services/api/modules/payroll/payroll-view'
import * as yup from 'yup'

const IncomeTaxAdjustmentModal = ({ children, data, time_id }) => {
  const [incomeTaxModalOpen, setIncomeTaxModalOpen] = useState(false)
  const handleClose = () => {
    setIncomeTaxModalOpen(false)
  }
  const handleOpen = () => {
    setIncomeTaxModalOpen(true)
  }
  const [isLoading, setIsLoading] = useState(false)

  // RECALCULATE
  const { mutate: recalculatePayroll } = useMutation(recalculateSelectedEmployee, {
    onSuccess: () => {
      queryClient.invalidateQueries('retrieveEmployeeData')
    },
    onError: (error) => {
      if (error.status === 422) {
        console.log(error)
      } else {
        Bugsnag.notify(error.data)
      }
    },
  })
  const queryClient = useQueryClient()
  const { mutate: mutateAdjustMtd } = useMutation(adjustMtdCalculation, {
    onSuccess: () => {
      setIncomeTaxModalOpen(false)
      setIsLoading(false)
      recalculatePayroll({ time_id, employee_id: data?.id, data: {} })
    },
    onError: (error) => {
      if (error.status === 422) {
        console.log(error)
      } else {
        Bugsnag.notify(error.data)
      }
    },
  })
  const validationSchema = yup.object().shape({
    custom_amount: yup
      .string()
      .nullable()
      .required('This field is required')
      .matches(
        /(^\d{1,3}(,\d{3})*(\.[0-9]{1,2})*$)|(^[0-9]*(\.[0-9]{1,2})*$)/,
        'This field only accept a valid number format'
      ),
    remarks: yup.string().nullable().required('This field is required'),
  })
  const customMtdSubmission = async (values) => {
    setIsLoading(true)
    mutateAdjustMtd({
      time_id,
      employee_id: data?.id,
      data: {
        custom_amount: values.custom_amount.replace(/,/g, ''),
        remarks: values.remarks,
      },
    })
    // dont forget to invalidate queries after submit
  }
  return (
    <>
      <Box onClick={handleOpen}>{children}</Box>
      <AdxFormModal
        title="Income tax adjustment"
        subtitle=""
        handleClose={handleClose}
        open={incomeTaxModalOpen}
        modalWidth={600}
      >
        <Formik
          validationSchema={validationSchema}
          initialValues={{
            custom_amount: data?.mtd?.nett_mtd,
            remarks: data?.mtd?.mtd_remarks ? data?.mtd?.mtd_remarks : '',
          }}
          onSubmit={customMtdSubmission}
        >
          {({ values, handleChange, touched, errors, handleBlur, handleSubmit }) => {
            return (
              <Form>
                <Box>
                  <Typography>
                    User may adjust the MTD calculation manually. However, Adnexio shall not be
                    responsible for any matters arising from the manual modification of this field
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: 'default-background.main',
                      p: 2,
                      my: 2,
                      borderRadius: 1,
                    }}
                  >
                    <EmployeeList
                      avatar={data?.avatar}
                      name={data?.name}
                      position={data?.position}
                    />
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={4}>
                    <Typography>Income tax</Typography>
                    <Field
                      name="custom_amount"
                      as={AdxInput}
                      startAdornment={<InputAdornment position="start">RM</InputAdornment>}
                      value={values?.custom_amount}
                      sx={{ width: '250px' }}
                      inputProps={{ min: 0 }}
                      onChange={(e) => handleChange(e)}
                      onBlur={handleBlur}
                      errorMessage={
                        <ErrorMessage name="custom_amount" component={AdxErrorMessage} />
                      }
                      isErrorShown={touched.custom_amount && errors.custom_amount}
                    />
                  </Box>
                  <Box my={4}>
                    <Field
                      name="remarks"
                      as={AdxInput}
                      multiline
                      minRows={2}
                      sxInput={{ padding: 2 }}
                      charLimit={120}
                      sx={{ width: 1 }}
                      label="Adjustment remarks"
                      value={values?.remarks}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errorMessage={<ErrorMessage name="remarks" component={AdxErrorMessage} />}
                      isErrorShown={errors.remarks && touched.remarks}
                    />
                  </Box>

                  <AdxButton variant={'contained'} fullWidth onClick={handleSubmit}>
                    {isLoading ? <CircularProgress color="white" size={20} /> : `Apply`}
                  </AdxButton>
                </Box>
              </Form>
            )
          }}
        </Formik>
      </AdxFormModal>
    </>
  )
}

export default IncomeTaxAdjustmentModal
